import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from 'config/axiosConfig';
import { transformHtmlContent } from 'util/transformHtmlContent';
import Report from 'components/Entities/Report';
import { ChevronsDown, ChevronsUp, Clock, Flag } from 'lucide-react';
import EntityActions from 'components/Entities/EntityActions';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from 'ui/Carousel';
import {
  Chip,
  Tooltip
} from '@mui/joy';
import { Ellipsis } from 'lucide-react';
import { Pencil } from 'lucide-react';
import { Trash } from 'lucide-react';
import { Link as LinkIcon } from 'lucide-react';
import { Impact } from 'util/constants';
import { formatDate, formatRelativeTime } from 'config/dayjsConfig';
import { useUser } from 'util/UserContext';
import EntityThumbnail from 'components/Entities/EntityThumbnail';
import EntityAvatar from 'components/Entities/EntityAvatar';
import { 
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from 'ui/DropdownMenu';
import { Drawer, DrawerContent, DrawerTrigger } from 'ui/Drawer';
import DialogConfirmation from 'ui/DialogConfirmation';
import TextEditor from 'components/TextEditor';
import { YouTubeEmbed, InstagramEmbed, XEmbed, TikTokEmbed, FacebookEmbed } from 'react-social-media-embed';
import CoauthorSelect, { CoauthorSuggestion } from 'components/Coauthors/CoauthorSelect';
import DetailPanel from 'ui/DetailPanel';
import EntityPrivacy from 'components/Entities/EntityPrivacy';
import { PrivacyLevel } from 'util/userUtils';
import { 
  loadCoauthorData, 
  handleCoauthorSelect as selectCoauthor, 
  handleCoauthorRemove as removeCoauthor 
} from 'util/entityUtils';

interface Props {
  impact: Impact;
  showMeta?: boolean;
  truncateDescription?: boolean;
  isImpactProfilePage?: boolean;
  isEntitySection?: boolean;
  showActions?: boolean;
  onVote?: () => void;
  editMode?: boolean;
  onDescriptionChange?: (value: string) => void;
  onCoauthorsChange?: (addedCoauthors: CoauthorSuggestion[], removedCoauthors: CoauthorSuggestion[]) => void;
  children?: React.ReactNode;
}

const ImpactCard = ({
  impact,
  showMeta = true,
  truncateDescription = true,
  isImpactProfilePage = false,
  isEntitySection = false,
  editMode = false,
  onDescriptionChange,
  onCoauthorsChange,
  children,
}: Props) => {
  const { currentUser, setShowAuthModal, userIsMobile } = useUser();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const impactHref = `/impact/${encodeURIComponent(impact.id)}`;
  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);  
  const [existingCoauthors, setExistingCoauthors] = useState<CoauthorSuggestion[]>(impact.properties?.coauthors || []);
  const [newCoauthors, setNewCoauthors] = useState<CoauthorSuggestion[]>([]);
  const [removedCoauthors, setRemovedCoauthors] = useState<CoauthorSuggestion[]>([]);
  const [pendingCoauthors, setPendingCoauthors] = useState<CoauthorSuggestion[]>([]);
  const [isLoadingCoauthors, setIsLoadingCoauthors] = useState<boolean>(false);
  const [privacy, setPrivacy] = useState<PrivacyLevel>((impact.properties?.privacy as PrivacyLevel) || 'public');
  const TruncateMarkup = require('react-truncate-markup').default;
 
  const IMAGE_CDN = process.env.REACT_APP_IMAGE_CDN;

  const handleCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const element = event.target as HTMLElement;
    if (element.tagName.toLowerCase() === 'a') return;    
    if (element.classList.contains('read-more')) return;    
    if (!isImpactProfilePage) {
      event.stopPropagation();
      navigate(impactHref);
    } else {
      event.preventDefault();            
      if (element.tagName.toLowerCase() === 'img' || element.tagName.toLowerCase() === 'svg' || element.tagName.toLowerCase() === 'path' || element.classList.contains('no-metadata-link')) {
          event.stopPropagation();
          window.open(impact.reference_link, '_blank', 'noopener,noreferrer');
      } else {            
          return;
      }
    }
  };

  const handleDescriptionClick = (event: React.MouseEvent) => {
    if (!truncateDescription || !isTruncated) return;
    event.preventDefault();
    if (isImpactProfilePage && truncateDescription) {
      setIsExpanded(!isExpanded);
    } else {
      return;
    }
  };

  const handleReadMoreClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setIsExpanded(!isExpanded);
    event.stopPropagation(); // Prevent the click from bubbling up to the card
  };

  const handleReportClick = () => {
    if (!currentUser) {
      setShowAuthModal(true);
    } else {
      setMenuOpen(false);
      setDrawerOpen(false);
      setIsReportModalOpen(true);
    }
  };

  const handleEdit = () => {
    setMenuOpen(false);
    setDrawerOpen(false);
    navigate(`/impact/${impact.id}?edit=true`);
  };

  const handleDelete = () => {
    setMenuOpen(false);
    setDrawerOpen(false);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/api/impacts/id/${impact.id}`);
      setIsDeleteDialogOpen(false);
      if (isImpactProfilePage) {
        navigate('/');
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error deleting impact:', error);
    }
  };

  const cancelDelete = () => {
    setIsDeleteDialogOpen(false);
  };

  const truncateEllipsis = (node: any) => {
    return (
      <span className="read-more">
      <button 
        className="read-more-border w-full text-sm rounded-md border-b-2 justify-center items-center px-1"
        onClick={handleReadMoreClick}
      >
      <span className="flex justify-center items-center ">
          <ChevronsDown className="flex justify-center items-center"/>
          </span>
        </button>
          </span>
    );
  };

  useEffect(() => {
    if (impact.id && currentUser?.id === impact.user_id) {
      loadCoauthorData(
        {
          id: impact.id,
          user_id: impact.user_id,
          properties: impact.properties
        },
        'impact',
        currentUser?.id,
        {
          setExistingCoauthors,
          setPendingCoauthors,
          setIsLoadingCoauthors
        }
      );
    }
  }, [impact.id, impact.user_id, impact.properties, currentUser?.id]);

  const handleCoauthorSelect = (suggestion: CoauthorSuggestion) => {
    selectCoauthor(
      suggestion,
      existingCoauthors,
      newCoauthors,
      setNewCoauthors,
      onCoauthorsChange,
      removedCoauthors
    );
  };

  const handleCoauthorRemove = async (coauthor: CoauthorSuggestion) => {
    await removeCoauthor(
      coauthor,
      {
        setNewCoauthors,
        setRemovedCoauthors,
        setPendingCoauthors,
        setExistingCoauthors
      },
      {
        newCoauthors,
        removedCoauthors
      },
      onCoauthorsChange
    );
  };

  const renderDescription = () => {
    if (truncateDescription && !isExpanded) {
      return (
      <TruncateMarkup lines={10} tokenize="words" ellipsis={truncateEllipsis} onTruncate={(wasTruncated: any) => setIsTruncated(wasTruncated)}>
        <div>
          {transformHtmlContent(impact.description || '')}
        </div>
      </TruncateMarkup>
      );
    } else {
      return (
          <div>
              {transformHtmlContent(impact.description || '')}
              {isTruncated && (
                  <>
                      <span className="flex">
                          <button 
                              className="read-more-border w-full text-sm rounded-md border-b-2 justify-center items-center px-1"
                              onClick={handleReadMoreClick}
                          >
                              <span className="flex justify-center items-center ">
                                  <ChevronsUp className="flex justify-center items-center" />
                              </span>
                          </button>
                      </span>
                  </>
              )}
          </div>
      );
    }
  };

  const getChipColor = (impact: Impact) => {
    return impact.rel_type === 'impact_positive' ? '#419357' : '#df7023';
  };

  const MoreButton = React.forwardRef<
    HTMLButtonElement,
    React.ButtonHTMLAttributes<HTMLButtonElement>
  >((props, ref) => (
    <button
      type="button"
      ref={ref}
      {...props}
      className="card-more size-7 rounded-full flex justify-center items-center"
    >
      <Ellipsis className="icon-stroke size-5"/>
    </button>
  ))
  MoreButton.displayName = 'MoreButton'

  const MoreOptions = () => (
    <>
      {!userIsMobile ? (
        <div className="flex flex-col">
          {currentUser?.id === impact.user_id && (
            <>
              <DropdownMenuItem onClick={handleEdit}>
                <div className="flex items-center gap-2 px-4 py-2 rounded-md text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                  <Pencil className="size-4 text-eoe-icon-stroke dark:text-eoe-dark-icon-stroke"/>
                  <span>Edit</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDelete}>
                <div className="flex items-center gap-2 px-4 py-2 rounded-md text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                  <Trash className="size-4 text-eoe-icon-stroke dark:text-eoe-dark-icon-stroke"/>
                  <span>Delete</span>
                </div>
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuItem onClick={handleReportClick}>
            <div className="flex items-center gap-2 px-4 py-2 rounded-md text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
              <Flag className="size-4 text-eoe-icon-stroke dark:text-eoe-dark-icon-stroke"/>
              <span>Report</span>
            </div>
          </DropdownMenuItem>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {currentUser?.id === impact.user_id && (
            <>
              <button onClick={handleEdit} className="flex mt-2 w-full items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                <Pencil className="size-4"/>
                <span>Edit</span>
              </button>
              <button onClick={handleDelete} className="flex w-full items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                <Trash className="size-4"/>
                <span>Delete</span>
              </button>
            </>
          )}
          <button onClick={handleReportClick} className="flex w-full items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
            <Flag className="size-4"/>
            <span>Report</span>
          </button>
        </div>
      )}
    </>
  )

  /*const getTikTokVideoId = (url: string) => {
    // Remove any query parameters
    const cleanUrl = url.split('?')[0];
    // Get the last segment of the URL
    return cleanUrl.split('/').pop();
  };*/

  const getSocialMediaEmbed = (url: string) => {
    if (!url) return null;
    
    const embedProps = {
      className: 'w-full flex-shrink-[2] md:max-w-[300px] md:max-h-[150px]',
      height: 'max-h-[50svh]',
      containerClassName: 'relative w-full h-full [&>iframe]:w-fit [&>iframe]:h-fit'
    };

    switch (true) {
      case url.includes('youtube.com'):
        return (
          <div className={embedProps.containerClassName}>
            <YouTubeEmbed url={url} youTubeProps={{ iframeClassName: embedProps.className }} />
          </div>
        );
      case url.includes('tiktok.com'):
        //const tiktokVideoId = getTikTokVideoId(url);
        //const tiktokUrl = `https://www.tiktok.com/player/v1/${tiktokVideoId}?&music_info=0&description=0`;
        return (
          <div className={embedProps.containerClassName + ' object-scale-down'}>
            <TikTokEmbed url={url} className={embedProps.className} />
          </div>
        );
      case url.includes('instagram.com'):
        return (
          <div className={embedProps.containerClassName + ' object-scale-down'}>
            <InstagramEmbed url={url} className={`w-full max-h-full md:max-w-[300px] md:max-h-[150px] object-scale-down`}/>
          </div>
        );
      case url.includes('x.com'):
        return  (
          <div className={`object-scale-down`}>
            <XEmbed url={url} height={600}/>
          </div>
        );
      case url.includes('twitter.com'):
        return (<XEmbed url={url} className={embedProps.className}/>);
      case url.includes('facebook.com'):
        return (
          <div className={embedProps.containerClassName + ' object-scale-down'}>
            <FacebookEmbed url={url} className={embedProps.className}/>
          </div>
        );
      default:

        return null;
    }
  };

  // Handle privacy change for impact
  const handlePrivacyChange = (newPrivacy: PrivacyLevel) => {
    setPrivacy(newPrivacy);
    
    // No need to call API here as the EntityPrivacy component handles the API call in edit mode
    // This just updates our local state to stay in sync
    
    // Also update the impact object's properties to reflect the new privacy setting
    if (impact.properties) {
      impact.properties.privacy = newPrivacy;
    } else {
      impact.properties = { privacy: newPrivacy };
    }
  };

  return (
    <div className="card-container my-1">
      {isReportModalOpen && (
        <Report
          entityId={impact.id}
          entityType="impact"
          open={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
        />
      )}
      <article>
        <div className={`rounded-lg flex flex-col px-2 md:px-3 py-2 relative ${isImpactProfilePage ? 'card-main-profile-page' : 'card-main'}`}>
          {showMeta && (
            <div className="entity-actions-container order-last">
              <EntityActions entity={impact} entityType="impact" />
            </div>
          )}
          <div className="card-content order-first">
            {showMeta && (
              <div className="card-meta text-sm flex items-center justify-between relative">
              <EntityAvatar 
                  entity={impact}
                  entityType="impact"
                  isEntityProfilePage={isImpactProfilePage}
                  isEntitySection={isEntitySection}
              />
                <div className="icon-more text-xl">
                  {userIsMobile ? (
                    <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
                      <DrawerTrigger asChild>
                        <MoreButton />
                      </DrawerTrigger>
                      <DrawerContent title="Actions">
                        <MoreOptions />
                      </DrawerContent>
                    </Drawer>
                  ) : (
                    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
                      <DropdownMenuTrigger asChild>
                        <MoreButton />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <MoreOptions />
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            )}
            <div
              className="card-header my-1"
              onClick={handleCardClick}
            >
              {isImpactProfilePage ? (
                <span className="card-title">
                  {impact.title}
                </span>
              ) : (
                <Link to={impactHref} className="card-title">
                  {impact.title}
                </Link>
              )}
              <div className="card-tags flex flex-row flex-wrap gap-2 my-2 py-2 items-center">
                <Chip
                  component={'div' as any}
                  label={`Impact ${impact.rel_type === 'impact_positive' ? '(+)' : '(-)'}`}
                  color="neutral"
                  variant="soft"
                  size="md"
                  sx={{
                    backgroundColor: getChipColor(impact),
                    color: '#fff',
                    fontSize: '0.75rem',
                  }}
                >
                  Impact {impact.rel_type === 'impact_positive' ? '(+)' : '(-)'}
                </Chip>
                {impact.scope && (
                  <Chip
                    component={'div' as any}
                    label={impact.scope}
                    color="neutral"
                    variant="soft"
                    size="md"
                    sx={{
                      backgroundColor: '#555555',
                      color: '#fff',
                      fontSize: '0.75rem',
                    }}
                  >
                    {impact.scope}
                  </Chip>
                )}
                <EntityPrivacy
                  privacy={privacy}
                  viewerRelation={impact.viewer_relation}
                  onPrivacyChange={() => {}}
                  actorType="user"
                  editMode={false}
                  entityId={impact.id}
                  entityType="impact"
                  miniIcon={true}
                  showDropdown={false}
                />
                <Tooltip title={formatDate(impact.created_at)} arrow>
                  <span className="card-date font-normal">
                    {formatRelativeTime(impact.created_at)}
                  </span>
                </Tooltip>
              </div>
            </div>
            <div className="flex flex-col-reverse md:grid md:grid-cols-3 md:grid-flow-row gap-2">
              {editMode ? (
                <>
                <div className="flex flex-wrap w-full justify-between items-center gap-2 mt-0.5">
                  <div className="flex-shrink-0">
                    <EntityPrivacy
                      privacy={privacy}
                      onPrivacyChange={handlePrivacyChange}
                      editMode={true}
                      entityId={impact.id}
                      entityType="impact"
                    />
                  </div>
                  <div className="flex flex-wrap gap-1 items-center">
                    {children}
                  </div>
                </div>
                  <div className="flex flex-col gap-2 md:col-span-2">
                    <TextEditor
                      value={impact.description}
                      onChange={(content: string) => {
                        onDescriptionChange?.(content);
                      }}
                      editorType="entity"
                      placeholder="Description"
                      isEditorExpanded={true}
                      action="edit"
                    >
                    </TextEditor>
                  </div>
                <DetailPanel 
                  title={`Collaborators (${existingCoauthors.length + pendingCoauthors.length})`} 
                  className="mb-2 text-sm bg-eoe-bg-modal dark:bg-eoe-dark-bg-modal"
                  defaultOpen={true}>
                    {isLoadingCoauthors ? (
                      <div className="p-2 text-center">Loading collaborators...</div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex flex-col gap-2">
                          <CoauthorSelect
                            inputType="add"
                            placeholder="Add collaborators"
                            className="w-full"
                            onSelect={handleCoauthorSelect}
                          />
                        </div>
                        {existingCoauthors.map((coauthor) => (
                          <EntityAvatar
                            key={`existing-${coauthor.type}-${coauthor.id}`}
                            entity={coauthor as any}
                            entityType={coauthor.type}
                            entityChip={true}
                            showCloseChip={editMode}
                            isInputField={false}
                            showLink={false}
                            onEntityChipClose={() => handleCoauthorRemove(coauthor)}
                          />
                        ))}
                        {pendingCoauthors.map((coauthor) => (
                          <EntityAvatar
                            key={`pending-${coauthor.type}-${coauthor.id}`}
                            entity={coauthor as any}
                            entityType={coauthor.type}
                            entityChip={true}
                            showCloseChip={editMode}
                            isInputField={false}
                            showLink={false}
                            badgeElement={<Clock className="size-3.5 text-yellow-500 dark:text-yellow-300" />}
                            onEntityChipClose={() => handleCoauthorRemove(coauthor)}
                          />
                        ))}
                        {newCoauthors.map((coauthor) => (
                          <EntityAvatar
                            key={`new-${coauthor.type}-${coauthor.id}`}
                            entity={coauthor as any}
                            entityType={coauthor.type}
                            entityChip={true}
                            showCloseChip={editMode}
                            isInputField={false}
                            showLink={false}
                            badgeElement={<Clock className="size-3.5 text-yellow-500 dark:text-yellow-300" />}
                            onEntityChipClose={() => handleCoauthorRemove(coauthor)}
                          />
                        ))}
                      </div>
                    )}
                </DetailPanel>
                </>
              ) : (
                <>
              <div className="card-description md:col-span-2" onClick={handleCardClick}>
                <span onClick={handleDescriptionClick}>
                    {renderDescription()}
                </span>
              </div>
                <div className="flex flex-wrap gap-2">
                  {(existingCoauthors.length || pendingCoauthors.length) > 0 &&
                    <DetailPanel 
                      title={`Collaborators (${existingCoauthors.length + pendingCoauthors.length})`} 
                      className="text-sm bg-eoe-bg-modal dark:bg-eoe-dark-bg-modal">
                      <Carousel className="border-none border-0">
                        <div className="flex justify-between ">
                          <>
                            <CarouselContent>
                                {existingCoauthors.map((coauthor) => (
                                  <CarouselItem key={`existing-${coauthor.type}-${coauthor.id}`} className="border-none basis-auto">
                                    <EntityAvatar
                                      key={`existing-${coauthor.type}-${coauthor.id}`}
                                      entity={coauthor as any}
                                      entityType={coauthor.type}
                                      entityChip={true}
                                      showCloseChip={false}
                                      isInputField={false}
                                      showLink={true}
                                    />
                                  </CarouselItem>
                                ))}
                                {pendingCoauthors.map((coauthor) => (
                                  <CarouselItem key={`pending-${coauthor.type}-${coauthor.id}`} className="border-none basis-auto">
                                    <EntityAvatar
                                      key={`pending-${coauthor.type}-${coauthor.id}`}
                                      entity={coauthor as any}
                                      entityType={coauthor.type}
                                      entityChip={true}
                                      showCloseChip={false}
                                      isInputField={false}
                                      badgeElement={<Clock className="size-3.5 text-yellow-500 dark:text-yellow-300" />}
                                      showLink={true}
                                    />
                                  </CarouselItem>
                                ))}
                            </CarouselContent>
                            <div className="flex flex-col justify-between">
                              <CarouselNext className="flex" size={'circle'}/>
                              <CarouselPrevious className="flex" size={'circle'}/>
                            </div>
                          </>
                        </div>
                      </Carousel>
                    </DetailPanel>
                  }
                </div>
                </>
              )}
              <div className={`no-metadata-link md:col-span-1 hover:cursor-pointer`}>
                {(() => {
                  const socialEmbed = getSocialMediaEmbed(impact.reference_link);
                  if (socialEmbed) {
                    return (
                      <div className="social-embed-container relative w-full h-full overflow-hidden">
                        {socialEmbed}
                      </div>
                    );
                  }
                  
                  if (impact.properties?.reference_link_img_key) {
                    return (
                      <EntityThumbnail 
                        url={`${IMAGE_CDN}/${impact.properties.reference_link_img_key}` || impact.properties?.reference_link_image_url}
                        urlType="img"
                        isProfilePage={isImpactProfilePage}
                        referenceLink={impact.reference_link}
                        onCardClick={handleCardClick}
                      />
                    );
                  }
                  
                  if (impact.reference_link) {
                    return (
                      <EntityThumbnail 
                        url={impact.reference_link}
                        urlType="page"
                        isProfilePage={isImpactProfilePage}
                        referenceLink={impact.reference_link}
                        onCardClick={handleCardClick}
                      />
                    );
                  }

                  return (
                    <div className="no-metadata-link flex justify-center items-center w-full min-w-[200px] min-h-[100px] md:h-full md:w-auto bg-eoe-icon dark:bg-eoe-dark-icon rounded-md" onClick={handleCardClick}>
                      <LinkIcon className="size-12 text-eoe-text-2 dark:text-eoe-dark-text-2" />
                    </div>
                  );
                })()}
              </div>
            </div>
            {impact.reference_link && (
              <div className="flex my-4">
                <a
                  className="external-link break-all text-xs pr-2"
                  href={impact.reference_link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {impact.reference_link}
                </a>
              </div>
            )}
          </div>
        </div>
      </article>      
      {isDeleteDialogOpen && (
        <DialogConfirmation 
          title="Delete Impact" 
          description="Are you sure you want to delete this impact?" 
          isOpen={isDeleteDialogOpen} 
          onConfirm={confirmDelete} 
          onCancel={cancelDelete}
        />
      )}
    </div>
  );
};

export default ImpactCard;
